import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaUserTag, FaPlus, FaTrashAlt, FaFolderPlus, FaUserCheck, FaUserSlash, FaBuilding } from 'react-icons/fa';

interface ActiveMember {
  id: number;
  name: string;
  email: string;
  company_name: string;
  profile?: string;
  sub_role?: string;
}

interface RoleItem {
  id: number;
  role_name: string;
  sub_category?: string;
  created_at?: string;
  assigned_member?: ActiveMember | null;
}

const RoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<RoleItem[]>([]);
  const [activeMembers, setActiveMembers] = useState<ActiveMember[]>([]);
  const [loading, setLoading] = useState(true);

  const [roleName, setRoleName] = useState('Coordinator');
  const [subCategory, setSubCategory] = useState('');
  const [customRoleName, setCustomRoleName] = useState('');
  const [initialMapUserId, setInitialMapUserId] = useState<number | ''>('');
  const [selectedMapUser, setSelectedMapUser] = useState<{ [roleId: number]: number }>({});

  const fetchRoles = async () => {
    try {
      const response = await axios.get<{ roles: RoleItem[]; active_members: ActiveMember[] }>('https://rmbbackend.rmbjaipur.com/auth/roles');
      setRoles(response.data.roles);
      setActiveMembers(response.data.active_members);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleAddRole = async (e: React.FormEvent) => {
    e.preventDefault();
    const finalRoleName = roleName === 'Other' ? customRoleName : roleName;
    const fullTitle = subCategory.trim() ? `${finalRoleName} - ${subCategory.trim()}` : finalRoleName;

    if (!finalRoleName.trim()) {
      alert('Please enter a role name');
      return;
    }

    try {
      await axios.post('https://rmbbackend.rmbjaipur.com/auth/roles', {
        role_name: finalRoleName,
        sub_category: subCategory.trim() || null,
      });

      if (initialMapUserId) {
        await axios.put('https://rmbbackend.rmbjaipur.com/auth/roles/map', {
          role_title: fullTitle,
          user_id: initialMapUserId
        });
      }

      alert('Role / Designation created and mapped successfully!');
      setSubCategory('');
      setCustomRoleName('');
      setInitialMapUserId('');
      fetchRoles();
    } catch (error) {
      console.error('Error adding role:', error);
      alert('Failed to add role.');
    }
  };

  const handleMapRole = async (role: RoleItem, userId: number | null) => {
    const fullTitle = role.sub_category ? `${role.role_name} - ${role.sub_category}` : role.role_name;

    try {
      await axios.put('https://rmbbackend.rmbjaipur.com/auth/roles/map', {
        role_title: fullTitle,
        user_id: userId
      });
      fetchRoles();
    } catch (error) {
      console.error('Error mapping role:', error);
      alert('Failed to map role to member.');
    }
  };

  const handleDeleteRole = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this role?')) {
      try {
        await axios.delete(`https://rmbbackend.rmbjaipur.com/auth/roles/${id}`);
        setRoles(prev => prev.filter(r => r.id !== id));
      } catch (error) {
        console.error('Error deleting role:', error);
        alert('Failed to delete role.');
      }
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-5 rounded-xl shadow-sm border border-gray-200 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <FaUserTag className="text-[#0A2451]" /> Role & Designation Management
          </h1>
          <p className="text-sm text-gray-500">Create official roles (Chairman, Secretary, Vice Chairman, Treasurer, Coordinators) and map them to active members.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Create Role Form */}
        <div className="lg:col-span-4 bg-white p-6 rounded-xl shadow-sm border border-gray-200 h-fit">
          <h2 className="text-lg font-bold text-gray-800 border-b pb-3 mb-4 flex items-center gap-2">
            <FaFolderPlus className="text-[#FDBC12]" /> Add Role / Position
          </h2>

          <form onSubmit={handleAddRole} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Select Base Role</label>
              <select
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-white font-medium focus:ring-2 focus:ring-blue-500 outline-none"
              >
                <option value="Chairman">Chairman</option>
                <option value="Vice Chairman">Vice Chairman</option>
                <option value="Secretary">Secretary</option>
                <option value="Treasurer">Treasurer</option>
                <option value="Coordinator">Coordinator</option>
                <option value="Other">Other (Custom Role)</option>
              </select>
            </div>

            {roleName === 'Other' && (
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Custom Role Name</label>
                <input
                  type="text"
                  placeholder="e.g. Director, Sergeant at Arms"
                  value={customRoleName}
                  onChange={(e) => setCustomRoleName(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                Sub-Category / Job Assignment <span className="text-gray-400 font-normal">(Optional)</span>
              </label>
              <input
                type="text"
                placeholder="e.g. Event Management, Membership Growth"
                value={subCategory}
                onChange={(e) => setSubCategory(e.target.value)}
                className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                Assign / Map Member <span className="text-gray-400 font-normal">(Optional)</span>
              </label>
              <select
                value={initialMapUserId}
                onChange={(e) => setInitialMapUserId(e.target.value ? Number(e.target.value) : '')}
                className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-white font-medium focus:ring-2 focus:ring-blue-500 outline-none"
              >
                <option value="">-- Do Not Map Now --</option>
                {activeMembers.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.company_name || 'RMB Member'})
                  </option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              className="w-full bg-[#0A2451] hover:bg-blue-900 text-white font-bold py-2.5 rounded-lg shadow transition flex items-center justify-center gap-2"
            >
              <FaPlus size={14} /> Add Role & Sub-Category
            </button>
          </form>
        </div>

        {/* Roles & Active Member Mapping Table */}
        <div className="lg:col-span-8 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h2 className="text-lg font-bold text-gray-800 border-b pb-3 mb-4">Official Roles & Mapped Active Members</h2>

          {loading ? (
            <p className="text-sm text-gray-500 text-center py-8">Loading roles & member list...</p>
          ) : roles.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No roles created yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-sm">
                <thead>
                  <tr className="bg-gray-100 border-b border-gray-200 text-gray-700 text-xs uppercase font-bold">
                    <th className="p-3">Position</th>
                    <th className="p-3">Mapped Active Member</th>
                    <th className="p-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {roles.map((r) => {
                    const fullTitle = r.sub_category ? `${r.role_name} - ${r.sub_category}` : r.role_name;

                    return (
                      <tr key={r.id} className="hover:bg-gray-50">
                        {/* Position Title */}
                        <td className="p-3 align-top">
                          <div className="font-bold text-[#0A2451] text-sm">{r.role_name}</div>
                          {r.sub_category && (
                            <span className="bg-amber-50 text-amber-900 border border-amber-200 font-semibold text-xs px-2 py-0.5 rounded-full inline-block mt-1">
                              {r.sub_category}
                            </span>
                          )}
                        </td>

                        {/* Mapped Member Cell */}
                        <td className="p-3 align-top">
                          {r.assigned_member ? (
                            <div className="flex items-center gap-3 bg-green-50 p-2 rounded-xl border border-green-200">
                              <img
                                className="h-10 w-10 object-cover rounded-full border border-green-400"
                                src={
                                  r.assigned_member.profile
                                    ? r.assigned_member.profile.startsWith('http')
                                      ? r.assigned_member.profile
                                      : `https://rmbbackend.rmbjaipur.com/${r.assigned_member.profile.replace(/\\/g, '/')}`
                                    : 'https://via.placeholder.com/150'
                                }
                                alt={r.assigned_member.name}
                              />
                              <div className="leading-tight flex-1">
                                <div className="font-bold text-gray-900 text-xs flex items-center gap-1">
                                  <FaUserCheck className="text-green-600" /> {r.assigned_member.name}
                                </div>
                                <div className="text-[11px] text-gray-600 flex items-center gap-1 mt-0.5">
                                  <FaBuilding className="text-gray-400 text-[10px]" /> {r.assigned_member.company_name}
                                </div>
                              </div>
                              <button
                                onClick={() => handleMapRole(r, null)}
                                className="text-red-500 hover:text-red-700 text-xs font-semibold px-2 py-1 bg-white border border-red-200 rounded hover:bg-red-50"
                                title="Unmap Member"
                              >
                                <FaUserSlash /> Unmap
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <select
                                value={selectedMapUser[r.id] || ''}
                                onChange={(e) => setSelectedMapUser({ ...selectedMapUser, [r.id]: Number(e.target.value) })}
                                className="p-2 border border-gray-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-blue-500 outline-none flex-1"
                              >
                                <option value="">-- Select Active Member --</option>
                                {activeMembers.map(user => (
                                  <option key={user.id} value={user.id}>
                                    {user.name} ({user.company_name || 'RMB Member'})
                                  </option>
                                ))}
                              </select>
                              <button
                                onClick={() => {
                                  const uid = selectedMapUser[r.id];
                                  if (!uid) {
                                    alert('Please select a member to map.');
                                    return;
                                  }
                                  handleMapRole(r, uid);
                                }}
                                className="bg-green-600 hover:bg-green-700 text-white font-bold text-xs px-3 py-2 rounded-lg shadow flex items-center gap-1"
                              >
                                <FaUserCheck /> Map
                              </button>
                            </div>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="p-3 align-top text-center">
                          <button
                            onClick={() => handleDeleteRole(r.id)}
                            className="text-red-500 hover:text-red-700 p-2 rounded hover:bg-red-50"
                            title="Delete Position"
                          >
                            <FaTrashAlt />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoleManagement;
